<script setup>
import { useForm, usePage, Link } from '@inertiajs/vue3'
import AppLayout from '@/Layouts/AppLayout.vue'
import AltchaField from '@/Components/Forms/AltchaField.vue'
import { Button } from '@/Components/ui/button'
import { Alert, AlertDescription } from '@/Components/ui/alert'
import { KeyRound, AlertCircle, CheckCircle2, ArrowLeft } from 'lucide-vue-next'
import { computed, ref } from 'vue'
import { store } from '@/routes/forgot-password'

const page = usePage()
const altchaField = ref(null)
const form = useForm({
  altcha: '',
  email: '',
})

const submit = () => {
  form.submit(store(), {
    onFinish: () => altchaField.value?.reset(),
  })
}

const status = computed(() => page.props.flash.status)
const hasErrors = computed(() => Object.keys(form.errors).length > 0)
</script>

<template>
  <AppLayout>
    <div class="flex items-center justify-center min-h-[calc(100vh-200px)] py-12 px-4 sm:px-6 lg:px-8">
      <div class="w-full max-w-md bg-white rounded-2xl shadow-lg p-10">
        <!-- Header -->
        <div class="mb-8">
          <div class="flex justify-center mb-6">
            <div class="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center text-white">
              <KeyRound class="w-6 h-6" />
            </div>
          </div>
          <h1 class="text-2xl font-bold text-gray-900 text-center mb-2">Forgot Password</h1>
          <p class="text-gray-600 text-center">Enter your email address and we'll send you a link to reset your password</p>
        </div>

        <form @submit.prevent="submit" class="space-y-6">
          <!-- General Error Alert -->
          <Alert v-if="hasErrors" variant="destructive">
            <AlertCircle class="h-4 w-4" />
            <AlertDescription>
              Please correct the errors below.
            </AlertDescription>
          </Alert>

          <!-- Success Status Alert -->
          <Alert v-if="status" class="border-green-500 text-green-700 [&>svg]:text-green-700">
            <CheckCircle2 class="h-4 w-4" />
            <AlertDescription>{{ status }}</AlertDescription>
          </Alert>

          <!-- Email Field -->
          <div>
            <label for="email" class="block text-sm font-semibold text-gray-700 mb-3">
              Email Address <span class="text-red-500">*</span>
            </label>
            <input
              id="email"
              type="email"
              v-model="form.email"
              required
              autofocus
              placeholder="john@example.com"
              :class="[
                'w-full px-5 py-4 border-2 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-gray-900',
                form.errors.email ? 'border-red-500' : 'border-gray-200'
              ]"
            />
            <p v-if="form.errors.email" class="text-sm text-red-500 mt-1">{{ form.errors.email }}</p>
          </div>

          <div>
            <AltchaField ref="altchaField" v-model="form.altcha" />
            <p v-if="form.errors.altcha" class="text-sm text-red-500 mt-1">{{ form.errors.altcha }}</p>
          </div>

          <Button class="w-full bg-purple-600 hover:bg-purple-700 h-auto px-5 py-4 text-base font-semibold rounded-xl" :disabled="form.processing || !form.altcha">
            Email Password Reset Link
          </Button>
        </form>

        <!-- Footer -->
        <div class="mt-8 text-center">
          <Link href="/login" class="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900">
            <ArrowLeft class="w-4 h-4 mr-2" />
            Back to login
          </Link>
        </div>
      </div>
    </div>
  </AppLayout>
</template>
